/**
 * Tenders — Appels d'Offres premium.
 *
 * 01/06/2026 — New tender (call-for-tenders / AO) subsystem replacing the
 * old comp-bid event modal.
 *
 * 02/06/2026 — Refacto user-feedback :
 *   • `durationDays` is now FIXED per tender (the client imposes it).
 *     The player can NO LONGER pick the duration via a slider; the only
 *     two negotiation levers are price and HSE commitment.
 *   • Added `competitors` (array of competitor names) and
 *     `competitorPriceRange` to power the new "Analyse concurrentielle"
 *     panel inside the application modal, gated by senior commercial
 *     level (Marc Dupuis or Sylvie Marchand). Léa Bertrand alone shows a
 *     "limited intelligence" disclaimer.
 *
 * Tier ordering :
 *   - 'local'    : Léa Bertrand (junior commercial) is enough
 *   - 'regional' : requires Marc Dupuis (senior, unlock 'salons')
 *   - 'national' : requires Sylvie Marchand (expert, unlock 'grands_comptes')
 *
 * Tender shape :
 *   id, clientName, sector, tier,
 *   durationDays         (FIXED — set by the client)
 *   baseDailyRevenue     client's reference price per truck-day
 *   requiresFleet        minimum total vehicles to qualify
 *   requiresRseTier      minimum EcoVadis tier (none/bronze/silver/gold)
 *   requiresMedicalPct   minimum % of compliant medical certs
 *   competitors          array of competitor names likely on this tender
 *   competitorPriceRange [low, high] expected competitor offer (€/day)
 *   narrative            { announce, win, loss }
 *
 * 10/02/2026 (Lot J1) — Rééquilibrage user prod : « Les AO doivent
 * suivre la progression de l'apprenant et être toujours légèrement
 * supérieurs aux contrats quotidiens. »
 *   • Local AO  (Léa, Tier 1)  : ~250 €/j vs contrats quotidiens Tier 1 ~200 €/j → +25 %
 *   • Regional  (Marc, Tier 2) : ~530 €/j vs contrats quotidiens Tier 2 ~400 €/j → +30 %
 *   • National  (Sylvie, T3+)  : ~1 220 €/j vs contrats quotidiens Tier 3 ~1 000 €/j → +20 %
 * Logique : un AO doit toujours valoir plus qu'un contrat quotidien
 * du même tier, car il engage la flotte sur 90-540 jours et bloque
 * d'autres opportunités. Les `competitorPriceRange` ont été déplacés
 * proportionnellement pour garder la même fenêtre de négociation.
 */

export const TENDERS = [
  // ── LOCAL tier — Léa Bertrand sufficient ─────────────────────────────────
  {
    id: 'tender-mairie-tours',
    clientName: 'Mairie de Tours',
    sector: 'public-local',
    tier: 'local',
    durationDays: 120,
    baseDailyRevenue: 260,
    requiresFleet: 3,
    requiresRseTier: 'none',
    requiresMedicalPct: 80,
    competitors: ['ÉcoRoute Services', 'Rapid\'Fret Solutions'],
    competitorPriceRange: [235, 280],
    narrative: {
      announce:
        "La Mairie de Tours lance un appel d'offres pour la livraison de fournitures " +
        "scolaires et de mobilier entre ses dépôts et les écoles primaires. Marché modeste " +
        "mais récurrent — idéal pour bâtir une référence secteur public. Le cahier " +
        "des charges fixe la durée à 120 jours.",
      win:
        "Excellente nouvelle : la Mairie de Tours retient votre proposition. Les premières " +
        "livraisons démarrent dans 15 jours, avec un cahier des charges précis sur les horaires.",
      loss:
        "La Mairie de Tours a notifié le rejet de votre proposition. Le prestataire retenu " +
        "était mieux positionné sur le critère prix. Ce n'est pas perdu — d'autres communes " +
        "lanceront bientôt leurs propres AO.",
    },
  },
  {
    id: 'tender-bricoland-regional',
    clientName: 'Bricoland Régional',
    sector: 'distribution',
    tier: 'local',
    durationDays: 90,
    baseDailyRevenue: 240,
    requiresFleet: 3,
    requiresRseTier: 'none',
    requiresMedicalPct: 70,
    competitors: ['TransNord Express', 'Rapid\'Fret Solutions'],
    competitorPriceRange: [225, 260],
    narrative: {
      announce:
        "Bricoland Régional (chaîne de magasins de bricolage, 18 points de vente) cherche " +
        "un transporteur pour son flux régional inter-magasins. Volume modéré, contraintes " +
        "horaires souples. Durée fixée à 90 jours. Bon contrat pour densifier votre carnet.",
      win:
        "Bricoland Régional valide votre offre. Première tournée prévue dans 10 jours, " +
        "leur acheteur a particulièrement apprécié votre flexibilité tarifaire.",
      loss:
        "Bricoland Régional a retenu un concurrent. Leur acheteur précise que votre " +
        "proposition était sérieuse mais qu'ils ont privilégié un prestataire déjà connu.",
    },
  },
  {
    id: 'tender-bio-frais-coop',
    clientName: 'Bio&Frais Coopérative',
    sector: 'agro-bio',
    tier: 'local',
    durationDays: 180,
    baseDailyRevenue: 290,
    requiresFleet: 3,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 90,
    competitors: ['ÉcoRoute Services', 'Frigo Régional Sud'],
    competitorPriceRange: [265, 310],
    narrative: {
      announce:
        "La coopérative Bio&Frais lance un AO pour la collecte des productions maraîchères " +
        "auprès de 40 producteurs locaux. Cahier des charges exigeant côté traçabilité et " +
        "respect chaîne du froid. Durée 180 jours. Un label EcoVadis est mentionné en " +
        "condition préférentielle.",
      win:
        "La coopérative Bio&Frais signe avec vous. Leurs adhérents ont apprécié votre " +
        "démarche RSE et la qualité de votre suivi formations chauffeurs.",
      loss:
        "Bio&Frais a tranché en faveur d'un transporteur frigorifique spécialisé. Notre " +
        "démarche RSE n'a pas suffi à compenser leur image établie sur ce segment.",
    },
  },

  // ── REGIONAL tier — requires Marc Dupuis (unlock 'salons') ───────────────
  {
    id: 'tender-cooperative-nouvelle-aquitaine',
    clientName: 'Coopérative Nouvelle-Aquitaine',
    sector: 'agro',
    tier: 'regional',
    durationDays: 270,
    baseDailyRevenue: 460,
    requiresFleet: 5,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 90,
    competitors: ['LogiStar International', 'Transports Bardin', 'AgriLog Sud'],
    competitorPriceRange: [430, 495],
    narrative: {
      announce:
        "La Coopérative agricole Nouvelle-Aquitaine ouvre un appel d'offres pour ses flux " +
        "céréales et engrais à l'échelle du grand Sud-Ouest. Marché disputé, vos concurrents " +
        "directs sont déjà positionnés — il faudra un dossier solide. Engagement 270 jours.",
      win:
        "Très belle prise commerciale : la Coopérative Nouvelle-Aquitaine valide votre " +
        "candidature. Le directeur logistique cite votre fiabilité financière et votre " +
        "politique RSE comme éléments différenciants.",
      loss:
        "La Coopérative a retenu un concurrent régional historique. Marc Dupuis estime " +
        "que notre dossier était bon mais que le critère 'ancienneté de la relation' a " +
        "pesé contre nous.",
    },
  },
  {
    id: 'tender-mecaero-industries',
    clientName: 'MecAero Industries',
    sector: 'aero',
    tier: 'regional',
    durationDays: 270,
    baseDailyRevenue: 540,
    requiresFleet: 6,
    requiresRseTier: 'silver',
    requiresMedicalPct: 95,
    competitors: ['LogiStar International', 'Transports Bardin', 'Aéro Express'],
    competitorPriceRange: [510, 580],
    narrative: {
      announce:
        "MecAero Industries (sous-traitant aéronautique Tier 1) prépare son plan transport " +
        "annuel : livraison just-in-time de pièces usinées vers Airbus et Safran. Marché à " +
        "haute valeur ajoutée, contraintes qualité ISO-9100, audits trimestriels. " +
        "Engagement ferme 270 jours.",
      win:
        "MecAero retient votre offre — c'est une référence majeure dans votre carnet. " +
        "Leur direction qualité a salué votre rigueur HSE et l'absence d'incidents " +
        "DREAL ces 12 derniers mois.",
      loss:
        "MecAero a sélectionné un prestataire concurrent disposant déjà d'une " +
        "certification ISO-9100. Marc Dupuis suggère d'investir dans la qualification " +
        "ISO pour rester compétitif sur ce segment.",
    },
  },
  {
    id: 'tender-vinotrans-bourgogne',
    clientName: 'VinoTrans Bourgogne',
    sector: 'luxe-vins',
    tier: 'regional',
    durationDays: 210,
    baseDailyRevenue: 590,
    requiresFleet: 5,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 90,
    competitors: ['LogiStar International', 'Œnotrans Premium'],
    competitorPriceRange: [560, 630],
    narrative: {
      announce:
        "Groupement VinoTrans (12 domaines bourguignons grand cru) ouvre un AO pour " +
        "leur logistique export. Marchandise sensible (3 000 €/palette), exigences fortes " +
        "côté assurance et traçabilité GPS. Engagement 210 jours.",
      win:
        "VinoTrans valide votre candidature. Leur président rappelle qu'un seul incident " +
        "casserait la relation immédiatement — sérieux et rigueur impératifs.",
      loss:
        "VinoTrans a finalement retenu un spécialiste œnologique. Leur DG précise que " +
        "votre dossier était professionnel mais qu'ils privilégient l'expertise sectorielle.",
    },
  },

  // ── NATIONAL tier — requires Sylvie Marchand (unlock 'grands_comptes') ───
  {
    id: 'tender-carrefour-supply-chain',
    clientName: 'Carrefour Supply Chain',
    sector: 'gms',
    tier: 'national',
    durationDays: 360,
    baseDailyRevenue: 1180,
    requiresFleet: 8,
    requiresRseTier: 'silver',
    requiresMedicalPct: 95,
    competitors: ['Géodis', 'LogiStar International', 'STEF Transport'],
    competitorPriceRange: [1130, 1260],
    narrative: {
      announce:
        "Carrefour Supply Chain lance le renouvellement de son lot 'navette inter-entrepôts " +
        "Ouest'. Volume garanti, paiements 30 jours, mais cahier des charges drastique : " +
        "EcoVadis Silver mini, flotte engagée 30 % bas-carbone à 24 mois, KPIs hebdomadaires. " +
        "Durée 360 jours, renouvelable.",
      win:
        "Énorme contrat décroché : Carrefour Supply Chain vous référence comme prestataire " +
        "officiel sur le lot Ouest. Sylvie Marchand a parfaitement piloté la phase BAFO. " +
        "Attendez-vous à des audits qualité réguliers.",
      loss:
        "Carrefour a tranché en faveur d'un concurrent national. Sylvie Marchand débrieffe : " +
        "notre score EcoVadis a fait la différence négativement, l'acheteur a privilégié un " +
        "candidat Gold.",
    },
  },
  {
    id: 'tender-sncf-fret',
    clientName: 'SNCF Fret — Pré-acheminement',
    sector: 'public-national',
    tier: 'national',
    durationDays: 450,
    baseDailyRevenue: 1100,
    requiresFleet: 7,
    requiresRseTier: 'silver',
    requiresMedicalPct: 95,
    competitors: ['Géodis', 'LogiStar International', 'DB Schenker'],
    competitorPriceRange: [1050, 1180],
    narrative: {
      announce:
        "SNCF Fret cherche des prestataires routiers pour le pré et post-acheminement " +
        "rail-route sur ses plateformes multimodales. Marché public au sens du code de la " +
        "commande publique : dossier administratif lourd, critères pondérés (prix 40 %, " +
        "RSE 30 %, technique 30 %). Engagement 450 jours.",
      win:
        "SNCF Fret valide votre référencement. C'est une référence prestigieuse — " +
        "ouverture quasi-garantie sur d'autres marchés publics dans les 12 mois.",
      loss:
        "SNCF Fret n'a pas retenu votre candidature. Sylvie Marchand analyse : votre note " +
        "technique était bonne mais le critère prix nous a pénalisés face à un opérateur " +
        "national low-cost.",
    },
  },
  {
    id: 'tender-grand-paris-amenagement',
    clientName: 'Grand Paris Aménagement',
    sector: 'public-btp',
    tier: 'national',
    durationDays: 540,
    baseDailyRevenue: 1380,
    requiresFleet: 10,
    requiresRseTier: 'gold',
    requiresMedicalPct: 100,
    competitors: ['Géodis', 'Lafarge Logistique', 'Vinci Construction Transports'],
    competitorPriceRange: [1320, 1450],
    narrative: {
      announce:
        "Grand Paris Aménagement lance son AO logistique chantiers Olympiques (matériaux " +
        "BTP + évacuation déblais). Le top critère est la conformité RSE : EcoVadis Gold " +
        "exigé, traçabilité carbone par tonne-km, audit annuel par cabinet indépendant. " +
        "Engagement long : 540 jours.",
      win:
        "Contrat historique sécurisé : Grand Paris Aménagement vous référence sur le lot " +
        "chantiers Olympiques. Sylvie Marchand a brillamment défendu le dossier — " +
        "l'engagement Gold a fait toute la différence.",
      loss:
        "Grand Paris Aménagement a privilégié un consortium plus expérimenté sur les " +
        "marchés publics olympiques. Notre dossier RSE était au niveau, mais l'absence " +
        "de références JO antérieures a pesé.",
    },
  },
];

export const TENDER_TIER_RANK = { local: 1, regional: 2, national: 3 };

/**
 * Returns the senior-most commercial level the player has hired :
 *   - 'expert'  → Sylvie Marchand (or unlock 'grands_comptes')
 *   - 'senior'  → Marc Dupuis (or unlock 'salons')
 *   - 'junior'  → Léa Bertrand
 *   - null      → no commercial hired (AO page hidden)
 */
export function getPlayerCommercialLevel(hiredSupport = {}) {
  if (hiredSupport['role-sylvie-marchand']) return 'expert';
  if (hiredSupport['role-marc-dupuis']) return 'senior';
  if (hiredSupport['role-lea-bertrand']) return 'junior';
  return null;
}

/**
 * Cross-walks the commercial level to the highest tender tier accessible.
 */
export function getMaxAccessibleTier(commercialLevel) {
  if (commercialLevel === 'expert') return 'national';
  if (commercialLevel === 'senior') return 'regional';
  if (commercialLevel === 'junior') return 'local';
  return null;
}
